/** HTML-mode custom code helpers (safe for server + client; no React). */

import {
	CUSTOM_CODE_ROOT_ATTR,
	type CustomCodeCssAudience,
	prepareLeadingStyleInStaticHtml,
} from "./customCodeCssScope";

export type CompiledHtmlData = {
	type: "html";
	html: string;
	css: string;
	js: string;
};

const DOCTYPE_RE = /^\s*<!DOCTYPE[^>]*>/i;
const HTML_DOCUMENT_RE = /<\s*(html|head|body)\b/i;

/** True when the HTML tab holds a full document instead of an embeddable fragment. */
export function isHtmlDocumentMarkup(html: string): boolean {
	const trimmed = html.trim();
	if (!trimmed) {
		return false;
	}
	return DOCTYPE_RE.test(trimmed) || HTML_DOCUMENT_RE.test(trimmed);
}

function mergeCssBlocks(...blocks: (string | undefined)[]): string {
	return blocks
		.map((block) => block?.trim() ?? "")
		.filter(Boolean)
		.join("\n");
}

function mergeJsBlocks(...blocks: (string | undefined)[]): string {
	return blocks
		.map((block) => block?.trim() ?? "")
		.filter(Boolean)
		.join("\n\n");
}

function extractStyleBlocks(source: string): string {
	const styles: string[] = [];
	const re = /<style\b[^>]*>([\s\S]*?)<\/style>/gi;
	for (const match of source.matchAll(re)) {
		if (match[1]?.trim()) {
			styles.push(match[1]);
		}
	}
	return styles.join("\n");
}

function stripScriptBlocks(source: string): string {
	return source.replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, "");
}

function stripStyleBlocks(source: string): string {
	return source.replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, "");
}

function extractInlineScripts(source: string): string {
	const scripts: string[] = [];
	const re = /<script\b[^>]*>([\s\S]*?)<\/script>/gi;
	for (const match of source.matchAll(re)) {
		if (match[1]?.trim()) {
			scripts.push(match[1]);
		}
	}
	return scripts.join("\n\n");
}

/**
 * Deterministic document → fragment conversion (regex only).
 * Must match on SSR (Node) and the browser so dangerouslySetInnerHTML hydrates.
 */
function normalizeHtmlDocument(source: string): {
	html: string;
	extraCss: string;
	inlineScripts: string;
} {
	let html = source.trim().replace(/^\s*<!DOCTYPE[^>]*>\s*/i, "");
	const extraCss = extractStyleBlocks(html);
	const inlineScripts = extractInlineScripts(html);

	const bodyMatch = html.match(/<body\b[^>]*>([\s\S]*?)<\/body>/i);
	if (bodyMatch) {
		html = bodyMatch[1] ?? "";
	} else {
		html = html.replace(/<\/?html\b[^>]*>/gi, "");
		html = html.replace(/<head\b[^>]*>[\s\S]*?<\/head>/gi, "");
		html = html.replace(/<\/?body\b[^>]*>/gi, "");
	}

	html = stripScriptBlocks(stripStyleBlocks(html)).trim();

	return { html, extraCss, inlineScripts };
}

/** Strip inline `<style>` / `<script>` from embed fragments; merge into css/js tabs. */
function normalizeHtmlFragment(source: string): {
	html: string;
	extraCss: string;
	inlineScripts: string;
} {
	const extraCss = extractStyleBlocks(source);
	const inlineScripts = extractInlineScripts(source);
	if (!(extraCss || inlineScripts)) {
		return { html: source, extraCss: "", inlineScripts: "" };
	}
	return {
		html: stripScriptBlocks(stripStyleBlocks(source)).trim(),
		extraCss,
		inlineScripts,
	};
}

function mergeExtractedScripts(js: string, inlineScripts: string): string {
	if (!inlineScripts) {
		return js;
	}
	return js.trim() ? mergeJsBlocks(js, inlineScripts) : inlineScripts;
}

/**
 * Convert a full HTML document (DOCTYPE/html/head/body) into an embeddable fragment.
 * Merges head `<style>` into css and inline `<script>` into js when the JS tab is empty.
 */
export function normalizeHtmlCustomCodeParts(parts: {
	html?: string;
	css?: string;
	js?: string;
}): { html: string; css: string; js: string } {
	let html = parts.html ?? "";
	let css = parts.css ?? "";
	let js = parts.js ?? "";

	const normalized = isHtmlDocumentMarkup(html)
		? normalizeHtmlDocument(html)
		: normalizeHtmlFragment(html);

	html = normalized.html;
	css = mergeCssBlocks(css, normalized.extraCss);
	js = mergeExtractedScripts(js, normalized.inlineScripts);

	return { html, css, js };
}

/**
 * Run tenant JS against a static snapshot inside `container` and return post-init innerHTML.
 * Used at compile time (browser) so persisted __staticHtml matches client JS output.
 */
export function materializeStaticHtmlSnapshot(
	parts: { html: string; css: string; js: string },
	container: HTMLElement,
	scopeId?: string
): string {
	const snapshot = buildStaticHtmlSnapshot(parts);
	if (!snapshot) {
		return "";
	}
	if (scopeId?.trim()) {
		container.setAttribute(CUSTOM_CODE_ROOT_ATTR, scopeId);
	}
	container.innerHTML = snapshot;
	clearHtmlCustomJsRunGuard(container);
	const js = parts.js?.trim() ?? "";
	if (js) {
		executeHtmlCustomJs(js, container);
		const materialized = container.innerHTML.trim();
		return injectStaticHtmlJsMaterializedMarker(
			materialized,
			hashHtmlCustomJsSource(js)
		);
	}
	return container.innerHTML.trim();
}

/** SSR snapshot: leading `<style>` + fragment safe for dangerouslySetInnerHTML. */
export function buildStaticHtmlSnapshot(parts: {
	html: string;
	css: string;
}): string {
	const normalized = normalizeHtmlCustomCodeParts(parts);
	let snapshot = "";
	if (normalized.css) {
		snapshot += `<style>${normalized.css}</style>`;
	}
	snapshot += normalized.html;
	return snapshot.trim();
}

/** Normalize persisted staticHtml snapshots (may contain full documents from older compiles). */
export function normalizeStaticHtmlForEmbed(
	staticHtml: string,
	scopeId?: string,
	audience: CustomCodeCssAudience = scopeId ? "builder" : "publish"
): string {
	const trimmed = staticHtml.trim();
	if (!trimmed) {
		return trimmed;
	}

	const stylePrefixMatch = trimmed.match(/^<style\b[^>]*>([\s\S]*?)<\/style>/i);
	const leadingCss = stylePrefixMatch?.[1] ?? "";
	const remainder = stylePrefixMatch
		? trimmed.slice(stylePrefixMatch[0].length).trim()
		: trimmed;

	let normalized = trimmed;
	if (isHtmlDocumentMarkup(remainder)) {
		normalized = buildStaticHtmlSnapshot({
			html: remainder,
			css: leadingCss,
		});
	} else if (stylePrefixMatch) {
		normalized = prepareLeadingStyleInStaticHtml(
			trimmed,
			scopeId ?? "",
			audience
		);
	}

	return normalized;
}

export function buildHtmlCompiledPayload(parts: {
	html?: string;
	css?: string;
	js?: string;
}): string {
	const normalized = normalizeHtmlCustomCodeParts(parts);
	return JSON.stringify({
		type: "html",
		html: normalized.html,
		css: normalized.css,
		js: normalized.js,
	} satisfies CompiledHtmlData);
}

export function parseCompiledHtmlData(
	compiledCode: string
): CompiledHtmlData | null {
	if (!compiledCode.trim()) {
		return null;
	}
	try {
		const parsed = JSON.parse(compiledCode) as unknown;
		if (
			!parsed ||
			typeof parsed !== "object" ||
			(parsed as { type?: string }).type !== "html"
		) {
			return null;
		}
		const row = parsed as Record<string, unknown>;
		const normalized = normalizeHtmlCustomCodeParts({
			html: typeof row.html === "string" ? row.html : "",
			css: typeof row.css === "string" ? row.css : "",
			js: typeof row.js === "string" ? row.js : "",
		});
		return {
			type: "html",
			...normalized,
		};
	} catch {
		return null;
	}
}

/** Marks hybrid/static container so tenant init (e.g. append nav) does not run twice. */
export const HTML_CUSTOM_JS_RUN_ATTR = "data-pb-custom-js-run";

/** Hidden marker inside persisted staticHtml when JS ran at compile/capture time. */
export const HTML_CUSTOM_JS_MATERIALIZED_ATTR =
	"data-pb-custom-js-materialized";

function hashHtmlCustomJsSource(js: string): string {
	let hash = 0;
	for (let i = 0; i < js.length; i++) {
		hash = Math.imul(31, hash) + js.charCodeAt(i);
		hash %= 2 ** 32;
	}
	return String(hash);
}

/** Clear JS run guard (e.g. when compiled JS changes or the block unmounts). */
export function clearHtmlCustomJsRunGuard(container: HTMLElement): void {
	container.removeAttribute(HTML_CUSTOM_JS_RUN_ATTR);
}

function buildStaticHtmlJsMaterializedMarker(fingerprint: string): string {
	return `<span ${HTML_CUSTOM_JS_RUN_ATTR}="${fingerprint}" ${HTML_CUSTOM_JS_MATERIALIZED_ATTR}="true" hidden aria-hidden="true" style="display:none!important;position:absolute;width:0;height:0;overflow:hidden"></span>`;
}

/** Embed compile-time JS fingerprint in persisted staticHtml (not on the live wrapper). */
export function injectStaticHtmlJsMaterializedMarker(
	html: string,
	fingerprint: string
): string {
	const trimmed = html.trim();
	if (!(trimmed && fingerprint)) {
		return trimmed;
	}
	const marker = buildStaticHtmlJsMaterializedMarker(fingerprint);
	if (trimmed.includes(marker)) {
		return trimmed;
	}
	return `${marker}${trimmed}`;
}

function readStaticHtmlJsMaterializedFingerprint(
	container: HTMLElement
): string | null {
	const marker = container.querySelector(
		`[${HTML_CUSTOM_JS_MATERIALIZED_ATTR}="true"][${HTML_CUSTOM_JS_RUN_ATTR}]`
	);
	return marker?.getAttribute(HTML_CUSTOM_JS_RUN_ATTR) ?? null;
}

/**
 * Reset DOM slots that tenant JS fills via append/innerHTML so a hybrid re-run
 * (listeners + structure) does not duplicate compile-time snapshot markup.
 */
export function prepareHtmlCustomJsRerun(container: HTMLElement): void {
	const nav = container.querySelector("#coda-nav");
	if (!nav?.querySelector(".ch-nav-btn")) {
		return;
	}
	const slot = nav as {
		replaceChildren?: () => void;
		innerHTML?: string;
	};
	if (typeof slot.replaceChildren === "function") {
		slot.replaceChildren();
		return;
	}
	if (typeof slot.innerHTML === "string") {
		slot.innerHTML = "";
	}
}

/** Run tenant JS with `container` and `document` in scope (hybrid SSR markup + js field). */
export function executeHtmlCustomJs(js: string, container: HTMLElement): void {
	const trimmed = js.trim();
	if (!trimmed) {
		return;
	}
	const fingerprint = hashHtmlCustomJsSource(trimmed);
	if (container.getAttribute(HTML_CUSTOM_JS_RUN_ATTR) === fingerprint) {
		return;
	}

	const snapshotFingerprint =
		readStaticHtmlJsMaterializedFingerprint(container);
	const snapshotAlreadyMaterialized =
		snapshotFingerprint === fingerprint ||
		(!snapshotFingerprint &&
			container.querySelector("#coda-nav .ch-nav-btn") !== null);

	if (snapshotAlreadyMaterialized) {
		prepareHtmlCustomJsRerun(container);
	}

	container.setAttribute(HTML_CUSTOM_JS_RUN_ATTR, fingerprint);

	const run = () => {
		const fn = new Function("container", "document", trimmed);
		const doc = typeof document === "undefined" ? undefined : document;
		fn(container, doc);
	};
	// Hybrid SSR markup is already in the tree; do not defer to DOMContentLoaded.
	const deferToDomContentLoaded =
		typeof document !== "undefined" &&
		document.readyState === "loading" &&
		!container.isConnected;
	if (deferToDomContentLoaded) {
		document.addEventListener("DOMContentLoaded", run, { once: true });
		return;
	}
	run();
}

export function resolveCustomCodeCompiledPayload(props: {
	__compiledCode?: string;
	__codeType?: "html" | "react" | "nextjs";
	__sourceCode?: string;
	__cssCode?: string;
	__jsCode?: string;
}): string {
	const existing =
		typeof props.__compiledCode === "string" ? props.__compiledCode.trim() : "";
	if (existing) {
		return existing;
	}
	const codeType = props.__codeType ?? "html";
	if (codeType !== "html") {
		return "";
	}
	const html = props.__sourceCode?.trim() ?? "";
	const css = props.__cssCode?.trim() ?? "";
	const js = props.__jsCode?.trim() ?? "";
	if (!(html || css || js)) {
		return "";
	}
	return buildHtmlCompiledPayload({
		html: props.__sourceCode ?? "",
		css: props.__cssCode ?? "",
		js: props.__jsCode ?? "",
	});
}
